/**
 * InvitationAcceptPage
 * Public page for accepting workspace invitations via token URL
 *
 * Features:
 * - Reads invitation token from URL
 * - Fetches and displays invitation details
 * - Accept flow for logged-in users
 * - Redirect to login for unauthenticated users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { workspaceService } from '../services/workspaceService';
import { cn } from '../components/design-system';
import { Button } from '../components/v2/ui/button';
import {
  Building2,
  Mail,
  User,
  Users,
  Check,
  X,
  Loader2,
  AlertCircle,
  LogIn,
  Calendar,
  Shield,
} from 'lucide-react';

/**
 * Format date for display
 */
const formatDate = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

/**
 * Check if invitation is expired
 */
const isExpired = (expiresAt) => {
  if (!expiresAt) return false;
  return new Date(expiresAt) < new Date();
};

/**
 * InvitationAcceptPage component
 */
export function InvitationAcceptPage() {
  const { token } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  // State
  const [invitation, setInvitation] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isAccepting, setIsAccepting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // Check authentication (simplified - in real app would use auth context)
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  // Check auth status on mount
  useEffect(() => {
    // Check for auth token in localStorage
    const authToken = localStorage.getItem('token') || localStorage.getItem('authToken');
    const userStr = localStorage.getItem('user');

    if (authToken && userStr) {
      try {
        const user = JSON.parse(userStr);
        setIsAuthenticated(true);
        setCurrentUser(user);
      } catch (e) {
        console.error('[InvitationAccept] Failed to parse user:', e);
      }
    }
  }, []);

  // Fetch invitation details
  useEffect(() => {
    const fetchInvitation = async () => {
      if (!token) {
        setError('Invalid invitation link');
        setIsLoading(false);
        return;
      }

      try {
        const data = await workspaceService.getInvitationByToken(token);
        setInvitation(data);
      } catch (err) {
        console.error('[InvitationAccept] Fetch error:', err);
        if (err.response?.status === 404) {
          setError('This invitation was not found or has been revoked.');
        } else if (err.response?.status === 410) {
          setError('This invitation has expired.');
        } else {
          setError(err.response?.data?.message || 'Failed to load invitation details.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvitation();
  }, [token]);

  // Handle accept invitation
  const handleAccept = useCallback(async () => {
    if (!isAuthenticated || !currentUser) {
      // Redirect to login with return URL stored in sessionStorage
      const returnUrl = `/invite/${token}`;
      sessionStorage.setItem('redirectAfterLogin', returnUrl);
      navigate('/login');
      return;
    }

    setIsAccepting(true);
    setError(null);

    try {
      await workspaceService.acceptInvitation(token, currentUser.id);
      setSuccess(true);

      // Redirect to workspace after delay
      setTimeout(() => {
        navigate(`/workspace/${invitation.workspaceId}`);
      }, 2000);
    } catch (err) {
      console.error('[InvitationAccept] Accept error:', err);
      setError(err.response?.data?.message || 'Failed to accept invitation. Please try again.');
    } finally {
      setIsAccepting(false);
    }
  }, [token, isAuthenticated, currentUser, invitation, navigate]);

  // Handle login redirect — store return URL in sessionStorage so Login.js picks it up
  const handleLoginRedirect = useCallback(() => {
    const returnUrl = `/invite/${token}`;
    sessionStorage.setItem('redirectAfterLogin', returnUrl);
    navigate('/login');
  }, [token, navigate]);

  // Loading state
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#0A0A0A]">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-[#E8B800]" />
          <p className="text-[#999999]">
            Loading invitation...
          </p>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !invitation) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0A0A]">
        <div className="max-w-md w-full p-8 rounded-[14px] border bg-[#111111]/30 border-[#1A1A1A]/50">
          <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6 bg-red-500/20">
            <AlertCircle className="w-8 h-8 text-red-400" />
          </div>
          <h1 className="text-xl font-semibold mb-2 text-[#F5F5F5]">
            Invitation Error
          </h1>
          <p className="mb-6 text-[#999999]">
            {error}
          </p>
          <Button onClick={() => navigate('/')}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  // Check if expired
  const expired = invitation && isExpired(invitation.expiresAt);
  const alreadyAccepted = invitation?.status === 'ACCEPTED';
  const declined = invitation?.status === 'DECLINED';
  const revoked = invitation?.status === 'REVOKED';

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-[#0A0A0A]">
      <div className="max-w-md w-full p-8 rounded-[14px] border bg-[#111111]/30 border-[#1A1A1A]/50">
        {/* Success State */}
        {success ? (
          <div className="text-center">
            <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 bg-green-500/20">
              <Check className="w-10 h-10 text-green-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2 text-[#F5F5F5]">
              Welcome to the Team!
            </h1>
            <p className="mb-4 text-[#999999]">
              You've joined {invitation?.workspace?.name || 'the workspace'}.
            </p>
            <p className="text-sm text-[#555555]">
              Redirecting to your workspace...
            </p>
          </div>
        ) : (
          <>
            {/* Header */}
            <div className="text-center mb-8">
              <div className="w-20 h-20 rounded-[14px] flex items-center justify-center mx-auto mb-4 bg-[#E8B800]/10">
                <Building2 className="w-10 h-10 text-[#E8B800]" />
              </div>
              <h1 className="text-2xl font-bold mb-2 text-[#F5F5F5]">
                You're Invited!
              </h1>
              <p className="text-[#999999]">
                You've been invited to join a workspace on SLAM.
              </p>
            </div>

            {/* Invitation Details */}
            <div className="p-4 rounded-[14px] mb-6 bg-[#111111]/50 border border-[#1A1A1A]/50">
              {/* Workspace */}
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-[14px] flex items-center justify-center bg-[#E8B800]/10">
                  <Users className="w-6 h-6 text-[#E8B800]" />
                </div>
                <div>
                  <h3 className="font-semibold text-[#F5F5F5]">
                    {invitation?.workspace?.name || invitation?.workspace?.teamName || 'Workspace'}
                  </h3>
                  <p className="text-sm text-[#999999]">
                    {invitation?.workspace?.leagueCode || invitation?.workspace?.sport}
                  </p>
                </div>
              </div>

              {/* Role */}
              {invitation?.persona && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-[14px] flex items-center justify-center text-xl bg-green-500/10">
                    {invitation.persona.icon || '\u{1F464}'}
                  </div>
                  <div>
                    <p className="text-sm text-[#555555]">
                      Your Role
                    </p>
                    <p className="font-medium text-[#F5F5F5]">
                      {invitation.persona.name}
                    </p>
                  </div>
                  <div className="ml-auto px-2 py-1 rounded text-xs font-medium bg-blue-500/20 text-blue-400">
                    {invitation.persona.defaultAccessLevel || 'STANDARD'}
                  </div>
                </div>
              )}

              {/* Invited by */}
              {invitation?.invitedBy && (
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 rounded-[14px] flex items-center justify-center bg-purple-500/10">
                    <User className="w-6 h-6 text-purple-400" />
                  </div>
                  <div>
                    <p className="text-sm text-[#555555]">
                      Invited by
                    </p>
                    <p className="font-medium text-[#F5F5F5]">
                      {invitation.invitedBy.firstName} {invitation.invitedBy.lastName}
                    </p>
                  </div>
                </div>
              )}

              {/* Expiration */}
              <div className="flex items-center gap-3">
                <div className={cn(
                  'w-12 h-12 rounded-[14px] flex items-center justify-center',
                  expired ? 'bg-red-500/10' : 'bg-[#E8B800]/10'
                )}>
                  <Calendar className={cn(
                    'w-6 h-6',
                    expired ? 'text-red-400' : 'text-[#E8B800]'
                  )} />
                </div>
                <div>
                  <p className="text-sm text-[#555555]">
                    {expired ? 'Expired on' : 'Expires on'}
                  </p>
                  <p className={cn(
                    'font-medium',
                    expired ? 'text-red-400' : 'text-[#F5F5F5]'
                  )}>
                    {formatDate(invitation?.expiresAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Custom Message */}
            {invitation?.message && (
              <div className="p-4 rounded-[14px] mb-6 border-l-4 bg-[#111111]/30 border-[#E8B800]">
                <p className="text-sm italic text-[#CCCCCC]">
                  "{invitation.message}"
                </p>
              </div>
            )}

            {/* Error Message */}
            {error && (
              <div className="flex items-center gap-2 p-3 rounded-lg mb-4 bg-red-500/10 text-red-400">
                <AlertCircle className="w-4 h-4 flex-shrink-0" />
                <span className="text-sm">{error}</span>
              </div>
            )}

            {/* Actions */}
            <div className="space-y-3">
              {expired ? (
                <div className="text-center py-4 text-[#555555]">
                  <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>This invitation has expired.</p>
                  <p className="text-sm mt-1">Please contact the workspace admin for a new invitation.</p>
                </div>
              ) : alreadyAccepted ? (
                <div className="text-center">
                  <div className="flex items-center justify-center gap-2 py-3 mb-4 text-green-400">
                    <Check className="w-5 h-5" />
                    <span className="font-medium">Already Accepted</span>
                  </div>
                  <Button
                    onClick={() => navigate(`/workspace/${invitation.workspaceId}`)}
                    className="w-full"
                  >
                    Go to Workspace
                  </Button>
                </div>
              ) : declined || revoked ? (
                <div className="text-center py-4 text-[#555555]">
                  <X className="w-8 h-8 mx-auto mb-2 opacity-50" />
                  <p>This invitation is no longer valid.</p>
                </div>
              ) : isAuthenticated ? (
                <>
                  <Button
                    onClick={handleAccept}
                    disabled={isAccepting}
                    className="w-full"
                    size="lg"
                  >
                    {isAccepting ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Accepting...
                      </>
                    ) : (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        Accept Invitation
                      </>
                    )}
                  </Button>
                  <p className="text-center text-sm text-[#555555]">
                    Logged in as {currentUser?.email}
                  </p>
                </>
              ) : (
                <>
                  <Button
                    onClick={handleLoginRedirect}
                    className="w-full"
                    size="lg"
                  >
                    <LogIn className="w-4 h-4 mr-2" />
                    Sign In to Accept
                  </Button>
                  <p className="text-center text-sm text-[#555555]">
                    You need to sign in to accept this invitation.
                  </p>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default InvitationAcceptPage;
